// Core packages
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// Third party packages
import { MatDialog, Sort } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { DdtsService } from 'src/app/modules/ddts/ddts.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { PrintQrDialogComponent } from 'src/app/shared/components/dialogs/print-qr-dialog/print-qr-dialog.component';
import { Ddt } from 'src/app/shared/models/ddt/ddt.model';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { DateTimeFormatPipe } from 'src/app/shared/pipes/date-format.pipe';
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from 'src/app/shared/services/helper.service';
import { ThemeService } from 'src/app/shared/services/theme.service';

@Component({
  selector: 'app-ddt-detail',
  templateUrl: './ddt-detail.page.html',
  styleUrls: ['./ddt-detail.page.scss']
})
export class DdtDetailComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  ddt: Ddt = new Ddt();

  detailCardIsLoading = true;
  deleteCardIsLoading = true;

  // BEGIN - Add ddt form
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.save'),
    spinnerSize: 19,
    raised: true,
    stroked: false,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-save',
      inline: true,
    },
  };

  printModuleBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printModule"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-print",
      inline: true
    }
  };

  printQrBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printLabel"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "accent",
    spinnerColor: "accent",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-qrcode",
      inline: true
    }
  };

  printDetailsBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.print"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-print",
      inline: true
    }
  };
  // END - Add ddt form

  dateTimeFormat: DateTimeFormatPipe;
  qrcodeData = "";
  singleDocsQty: number;
  singleFilesQty: number;
  multiDocsQty: number;
  multiFilesQty: number;
  ddtDocuments: any[];
  sortedData = [];

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private ddtsService: DdtsService,
    private router: Router,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    public dialog: MatDialog,
    private cookieService: CookieService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('ddts.detail.pageTitle'));

    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));
    this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(res => this.loggedUserPermissions = res));

    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    // Get requested ddt data
    const ddtId = this.activatedRoute.snapshot.params.ddtId;
    this.subscriptions.push(
      this.ddtsService.get(ddtId).subscribe(
        res => {
          if (res.status) {
            // Load additional info
            this.loadData(res.data);

            // Make detail form card visible by disabling his card-spinner
            this.detailCardIsLoading = false;
            return;
          }

          // Something bad happened
          // redirect user to ddts list
          this.router.navigate([this.lang, 'app', 'ddts', 'list']);
        },
        err => {
          // if we came here users with given userId do not exist
          this.helperService.handleError(err);
          this.router.navigate([this.lang, 'errors', '404']);
        }
      )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Load DDT data
   *
   * @since 1.0.0
   */
  loadData(data: any) {
    if (!data) {
      return;
    }
    // Set ddt data
    this.ddt.setData(data.ddt);
    this.qrcodeData = JSON.stringify(data.ddt.code);

    if (data.ddtDocuments) {
      this.ddtDocuments = data.ddtDocuments;
    }

    this.singleDocsQty = 0;
    this.singleFilesQty = 0;
    this.multiDocsQty = 0;
    this.multiFilesQty = 0;

    this.ddtDocuments.forEach(doc => {
      if (doc.files && doc.numFiles) {
        if (doc.ddt.toString() !== this.ddt.getId().toString()) {
          this.toastrService.error("Errore: uno dei file non fa parte di questo DDT")
          return;
        } else if (doc.numFiles <= 0) {
          this.toastrService.error("Errore: un valore numFiles è invalido")
          return;
        } else if (doc.numFiles === 1) {
          // Documento unico
          this.singleDocsQty++;
          this.singleFilesQty++;
        } else if (doc.numFiles > 1) {
          // Documento multiplo
          this.multiDocsQty++;
          this.multiFilesQty += doc.numFiles;
        }
      }
    });

    this.sortedData = this.ddtDocuments;
  }

  /**
   * Get ddtDocument status basing on contained files
   *
   * @since 1.0.0
   */
  getStatus(files: any[]): string {
    if (!files || files && !files.length) {
      return;
    }
    if (files.some(el => el.status === 'missing')) {
      return 'missing';
    } else if (files.every(el => el.status === 'uploaded')) {
      return 'uploaded';
    }
  }


  /**
   * Handle balance status change
   *
   * @since 1.0.0
   */
  onStatusChange() {
    const previousStatus = this.ddt.get("status");
    let newStatus: string;
    if (!previousStatus) {
      return;
    }
    if (previousStatus === "open") {
      newStatus = "closed";
    } else if (previousStatus === "closed") {
      this.toastrService.warning("Questo DDT è già chiuso. Non è possibile modificarne lo stato", "Attenzione");
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.confirm.title'),
        message: this.translateService.instant('ddts.status.changeMessage'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.undo'),
      },
      width: '500px',
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res => {
          if (!res) {
            // User hit decline button
            return;
          }

          // User hit confirm button
          this.subscriptions.push(
            this.ddtsService.update(this.ddt.getId(), { status: newStatus })
              .subscribe(
                res2 => {
                  if (res2.status && res2.data && res2.data.ddt) {
                    this.toastrService.success(res2.message);
                    this.loadData(res2.data);
                  } else if (res2.status && res2.data) {
                    // API v2 answers the updated ddt alone, without its documents
                    this.toastrService.success(res2.message);
                    this.ddt.setData(res2.data);
                  }
                },
                err => {
                  this.helperService.handleError(err);
                }
              )
          );
        }
      );
  }

  /**
   * Handle print file qrCode
   *
   * @since 1.0.0
   */
  onPrint(target: string, doc: any): void {
    const qrCodes = [];
    if (doc.files && doc.files.length) {
      doc.files.forEach(file => {
        if (file.name) {
          qrCodes.push(file.name);
        }
      });
    }
    if (!qrCodes.length) {
      this.toastrService.error("No qrCodes to inject");
      return;
    }

    let data;

    if (target === "qr") {
      data = {
        target,
        qrCodes,
        companyName: doc.property.admin.company.name,
        propertyName: doc.property.name,
        propertyCity: doc.property.address.city,
        propertyAddress: `${doc.property.address.route}, ${doc.property.address.streetNumber}`,
        area: doc.area,
        docs: doc.docsSelected,
      };
    } else if (target === "module") {
      const structures = [];
      doc.docsSelected.forEach(element => {
        if (Array.isArray(element.structureSelected)) {
          element.structureSelected.forEach(subElement => {
            if (!structures.includes(subElement)) {
              structures.push(subElement);
            }
          });
        }
      });

      data = {
        target,
        qrCodes,
        companyName: doc.property.admin.company.name,
        propertyName: doc.property.name,
        propertyCity: doc.property.address.city,
        propertyAddress: `${doc.property.address.route}, ${doc.property.address.streetNumber}`,
        area: doc.area,
        uploadDate: doc.createdAt,
        content: doc.content,
        docs: doc.docsSelected,
        structures,
      };
    } else {
      this.toastrService.error("missing print target");
    }

    // Open dialog
    this.dialog.open(PrintQrDialogComponent, {
      data,
      maxWidth: "max-content",
    });
  }

  /**
    * Handle table sort
    *
    * @since 1.0.0
    */
  sortData(sort: Sort): any {
    const data = this.sortedData;
    if (!sort.active || sort.direction === '') {
      this.sortedData = data;
      return;
    }

    this.sortedData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'master': return this.compare(a.property.admin.company.name, b.property.admin.company.name, isAsc);
        case 'property': return this.compare(a.property.name, b.property.name, isAsc);
        case 'code': return this.compare(a.code, b.code, isAsc);
        case 'numFiles': return this.compare(a.numFiles, b.numFiles, isAsc);
        case 'status': return this.compare(a.status, b.status, isAsc);
        default: return 0;
      }
    });
  }

  /**
   * Utility per table sort
   *
   * @since 1.0.0
   */
  compare(a: number | string, b: number | string, isAsc: boolean): any {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  onPrintDetails() {
    this.printDetailsBtnOptions.active = true;
    const contentToPrint = document.getElementById("content-to-print").outerHTML;
    const originalContent = document.body;
    if (contentToPrint) {
      document.body.outerHTML = contentToPrint;
      window.print();
      document.body = originalContent;
      this.printDetailsBtnOptions.active = false;
    }
  }
}
